import { jsx as _jsx } from "react/jsx-runtime";
/**
 * @fileoverview CategoryBadge Component
 *
 * Badge component for displaying event categories with consistent color theming.
 * Supports 6 event categories (music, sports, food, tech, art, drinks) with
 * multiple size and variant options.
 *
 * @author LOCI Development Team
 * @since 1.1.0
 */
import { forwardRef, useMemo } from 'react';
import { useTheme } from '../../theme';
/**
 * Size configurations for CategoryBadge
 */
const SIZE_CONFIG = {
    xs: { height: 'h-5', padding: 'px-1.5', fontSize: 'text-xs', gap: 'gap-0.5' },
    sm: { height: 'h-6', padding: 'px-2', fontSize: 'text-xs', gap: 'gap-1' },
    md: { height: 'h-7', padding: 'px-2.5', fontSize: 'text-sm', gap: 'gap-1' },
    lg: { height: 'h-8', padding: 'px-3', fontSize: 'text-base', gap: 'gap-1.5' },
};
/**
 * Category display names
 */
const CATEGORY_LABELS = {
    music: 'Music',
    sports: 'Sports',
    food: 'Food',
    tech: 'Tech',
    art: 'Art',
    drinks: 'Drinks',
};
/**
 * CategoryBadge Component
 *
 * Displays event category badges with consistent theming and accessibility.
 */
export const CategoryBadge = forwardRef(({ category, size = 'md', variant = 'subtle', showLabel = true, className = '', 'data-testid': testId, 'aria-label': ariaLabel, }, ref) => {
    const theme = useTheme();
    const categoryColors = theme.category[category];
    const sizeConfig = SIZE_CONFIG[size];
    // Memoize variant classes based on categoryColors and variant
    const variantClasses = useMemo(() => {
        switch (variant) {
            case 'filled':
                return 'text-white';
            case 'outlined':
                return 'bg-transparent';
            case 'subtle':
            default:
                return '';
        }
    }, [variant]);
    const classes = useMemo(() => [
        'inline-flex items-center justify-center',
        'font-medium rounded-full',
        'border transition-colors duration-200',
        sizeConfig.height,
        sizeConfig.padding,
        sizeConfig.fontSize,
        sizeConfig.gap,
        variantClasses,
        className,
    ]
        .filter(Boolean)
        .join(' '), [sizeConfig, variantClasses, className]);
    const styles = useMemo(() => {
        switch (variant) {
            case 'filled':
                return {
                    backgroundColor: categoryColors.primary,
                    color: categoryColors.primaryFg,
                    borderColor: 'transparent',
                };
            case 'outlined':
                return {
                    backgroundColor: 'transparent',
                    color: categoryColors.primary,
                    borderColor: categoryColors.primary,
                };
            case 'subtle':
            default:
                return {
                    backgroundColor: categoryColors.light,
                    color: categoryColors.dark,
                    borderColor: 'transparent',
                };
        }
    }, [variant, categoryColors]);
    const label = CATEGORY_LABELS[category];
    return (_jsx("span", { ref: ref, className: classes, style: styles, "data-testid": testId || `category-badge-${category}`, role: "img", "aria-label": ariaLabel || `Category: ${label}`, children: showLabel && _jsx("span", { className: "whitespace-nowrap select-none", children: label }) }));
});
CategoryBadge.displayName = 'CategoryBadge';
